import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    /*
      Nunca carregamos o Quick Add com uma variante pré-escolhida.
      Isso elimina o P automático vindo do card.
    */
    url.searchParams.delete('variant');

    return url.toString();
  }

  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(currentUrl);

      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');

        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => this.toggleAttribute('stay-visible', false),
      { once: true }
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') return null;
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;

      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');

      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);

      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);

      productDetails.remove();
    }

    morph(modalContent, productGrid);

    /*
      Mantemos sincronização de outras opções do card,
      mas em seguida limpamos especificamente TAMANHO.
    */
    this.#syncVariantSelection(modalContent);
    this.#requireExplicitSizeSelection(modalContent);
  }

  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');

    for (const input of modalInputs) {
      if (
        input instanceof HTMLInputElement &&
        input.dataset.variantId === selectedVariantId &&
        !input.checked
      ) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }

  #findSizeFieldset(root) {
    const fieldsets = root.querySelectorAll('fieldset.variant-option, .variant-option');

    for (const fieldset of fieldsets) {
      const legend = fieldset.querySelector('legend');
      const text = legend?.textContent?.trim().toLowerCase() || '';

      if (text.includes('tamanho') || text.includes('size')) {
        return fieldset;
      }
    }

    return null;
  }

  #requireExplicitSizeSelection(modalContent) {
    const sizeFieldset = this.#findSizeFieldset(modalContent);
    if (!sizeFieldset) return;

    const resetState = () => {
      const currentSizeFieldset = this.#findSizeFieldset(modalContent);
      if (!currentSizeFieldset) return;

      const sizeInputs = currentSizeFieldset.querySelectorAll('input[type="radio"]');

      sizeInputs.forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.checked = false;
        }
      });

      const form = modalContent.querySelector('form[data-type="add-to-cart-form"]');
      const variantInput = form?.querySelector('input[name="id"]');
      const addButton = form?.querySelector('.add-to-cart-button, button[name="add"]');

      if (variantInput instanceof HTMLInputElement) {
        variantInput.disabled = true;
        variantInput.dataset.mvRequiresExplicitSize = 'true';
      }

      if (addButton instanceof HTMLButtonElement) {
        addButton.disabled = true;
        addButton.dataset.mvRequiresExplicitSize = 'true';

        const textContent = addButton.querySelector('.add-to-cart-text__content');

        if (textContent instanceof HTMLElement) {
          if (!textContent.dataset.mvOriginalText) {
            textContent.dataset.mvOriginalText = textContent.textContent?.trim() || 'ADICIONAR AO CARRINHO';
          }

          textContent.textContent = 'SELECIONE O TAMANHO';
        }
      }
    };

    resetState();

    if (modalContent.dataset.mvSizeGuardReady === 'true') return;
    modalContent.dataset.mvSizeGuardReady = 'true';

    modalContent.addEventListener('change', (event) => {
      const input = event.target;

      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

      const currentSizeFieldset = this.#findSizeFieldset(modalContent);
      if (!currentSizeFieldset || !currentSizeFieldset.contains(input)) return;

      window.setTimeout(() => {
        const form = modalContent.querySelector('form[data-type="add-to-cart-form"]');
        const variantInput = form?.querySelector('input[name="id"]');
        const addButton = form?.querySelector('.add-to-cart-button, button[name="add"]');

        if (variantInput instanceof HTMLInputElement) {
          variantInput.disabled = false;
          delete variantInput.dataset.mvRequiresExplicitSize;
        }

        if (addButton instanceof HTMLButtonElement) {
          /*
            Se o próprio tema marcou indisponível, respeitamos.
            Para tamanho disponível, liberamos o CTA.
          */
          if (input.dataset.optionAvailable !== 'false') {
            addButton.disabled = false;
          }

          delete addButton.dataset.mvRequiresExplicitSize;

          const textContent = addButton.querySelector('.add-to-cart-text__content');

          if (textContent instanceof HTMLElement) {
            textContent.textContent =
              textContent.dataset.mvOriginalText || 'ADICIONAR AO CARRINHO';
          }
        }
      }, 0);
    });
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });

    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );

    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (
      this.querySelector('.view-product-title a')
    );

    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (
      this.querySelector('.product-header a')
    );

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();

    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      const grid = document.querySelector('#ResultsList [product-grid-view]');

      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;

        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
